/**
 * Die vier Zahlen der Entwicklung auf Zuhause — nur Konto 1. (29.09.2026, Ticket 2733)
 *
 * Martin: *„Ein kleiner Live-Ticker nur für mich, der ständig sichtbar zeigt,
 * was in der Entwicklung passiert: wie viele Tickets heute erledigt wurden,
 * wie viele gerade in Arbeit sind, wie viele noch offen sind. Ziel: mehr
 * Übersicht, ohne nachfragen zu müssen."*
 *
 * Vier Zahlen, gezählt am Server (server/entwicklungs-ticker.ts) — der
 * Browser zählt nichts selbst.
 *
 * Wanderung: erst im Kopf (29.09.), seit 08.10.2026 (Richtung C) in der Lage unter
 * der Glocke, seit 11.10.2026 AUF ZUHAUSE (Ticket 3082, Martin: „kommt aus dem Header
 * heraus und wandert ins Zuhause, wo er ohnehin immer wieder nachschaut"). Eine
 * ruhige Zeile am Ende des Blatts, über der Lage-Zeile: Zahl oben, Wort darunter.
 * Die Lage unter der Glocke trägt sie nicht mehr.
 *
 * Das Blatt baut die Zeile (`baueEntwicklung`); dieses Modul füllt sie. Es merkt sich
 * die letzten Zahlen, damit ein neu aufgebautes Zuhause sofort Zahlen zeigt. Er bleibt
 * EIN Besitzer für `hidden` der Zeile — „da" heißt: hat eine gültige Zählung.
 *
 * ⚠️ Akku (Ticket 2752): höchstens jede Minute, und nur bei sichtbarer
 *    Seite — `starteSichtbarenTakt`, kein eigener setInterval. Ein Handy mit
 *    dunklem Display fragt gar nicht.
 * ⚠️ EIN Besitzer für `hidden`: nur diese Datei. Die Zeile erscheint mit der
 *    ersten gültigen Zählung und geht weg, sobald der Server sie verweigert
 *    (403) — dann hört auch das Fragen auf.
 * ⚠️ Nur zeigen, was aktuell ist (Ticket 2848, Martin 30.09.: „Sie nur anzeigen, wenn sie
 *    aktuell sind“): Scheiterte die Frage (kein Netz, Serverfehler), blieb bis dahin der ALTE
 *    Stand stehen — Zahlen von gestern sahen aus wie von jetzt. Jetzt gilt eine Zählung
 *    `AKTUELL_MS` (3 Minuten: zwei verpasste Minutentakte) und verschwindet dann samt Zeile; die
 *    nächste gute Antwort bringt sie zurück. Geprüft wird vor jeder Frage (auch beim Zurückkehren
 *    in die App, bevor die Antwort da ist), nach jeder gescheiterten Frage und beim Bauen einer Zeile.
 *    Die Bedeutung steht seit Ticket 3082 als Wort unter jeder Zahl (auch am Telefon) und im
 *    Satz der Zeile (`title`, `aria-label`).
 */
import { starteSichtbarenTakt } from "./abfrage-takt.js";

export interface TickerZahlen {
  erledigtHeute: number;
  inArbeit: number;
  offen: number;
  wartetAufDich: number;
}

/** Höchstens so oft wird gefragt (und nur bei sichtbarer Seite). */
const TICKER_TAKT_MS = 60_000;

/** So lange (ms) gilt eine Zählung als aktuell: zwei verpasste Minutentakte, dann ist sie alt. */
export const AKTUELL_MS = 3 * TICKER_TAKT_MS;

/** Ist eine Zählung von `gezaehltUm` zur Zeit `jetzt` (beides ms) noch aktuell? Reine Rechnung. */
export function istAktuell(gezaehltUm: number, jetzt: number): boolean {
  return jetzt - gezaehltUm <= AKTUELL_MS && jetzt >= gezaehltUm;
}

const POSTEN: readonly (keyof TickerZahlen)[] = ["erledigtHeute", "inArbeit", "offen", "wartetAufDich"];

/** Die Antwort des Servers als Zahlen — oder null, wenn eine fehlt. */
function zahlenAus(d: unknown): TickerZahlen | null {
  if (!d || typeof d !== "object" || (d as { ok?: unknown }).ok !== true) return null;
  const z = {} as TickerZahlen;
  for (const k of POSTEN) {
    const n = (d as Record<string, unknown>)[k];
    if (typeof n !== "number" || !Number.isInteger(n) || n < 0) return null;
    z[k] = n;
  }
  return z;
}

/** „warten auf dich“ oder, bei genau einem, „wartet auf dich“. */
function wartetWort(n: number): string {
  return n === 1 ? "wartet auf dich" : "warten auf dich";
}

/** Der ganze Satz — für Vorleser und als Hinweis unter der Maus. */
function tickerSatz(z: TickerZahlen): string {
  return (
    `Entwicklung: heute ${z.erledigtHeute} erledigt, ${z.inArbeit} in Arbeit, ` +
    `${z.offen} offen, ${z.wartetAufDich} ${wartetWort(z.wartetAufDich)}.`
  );
}

/** Eine Zahl, die in die Zeile passt: ab 100 „99+“. */
function kurz(n: number): string {
  return n > 99 ? "99+" : String(n);
}

const WORTE: Record<keyof TickerZahlen, string> = {
  erledigtHeute: "heute erledigt",
  inArbeit: "in Arbeit",
  offen: "offen",
  wartetAufDich: "warten auf dich",
};

const SLOT = "[data-slot=entwicklung]";

/** Die letzte gültige Zählung — ein neu gebautes Zuhause zeigt sie sofort. */
let letzte: TickerZahlen | null = null;
/** Wann `letzte` gezählt wurde (`Date.now()`). */
let letzteUm = 0;
/** Hat der Server das Konto abgelehnt (403)? Dann bleibt die Zeile weg. */
let verweigert = false;

/**
 * Die Zeile für Zuhause bauen: vier Felder (Zahl über Wort), versteckt, bis es Zahlen gibt.
 * Reihenfolge wie bisher in der Lage: warten auf dich, in Arbeit, offen, heute erledigt.
 */
export function baueEntwicklung(): HTMLElement {
  const el = document.createElement("section");
  el.className = "zuhause-entwicklung";
  el.setAttribute("data-slot", "entwicklung");
  el.setAttribute("aria-label", "Entwicklung");
  const liste = document.createElement("ul");
  liste.className = "zuhause-entwicklung-liste";
  for (const k of ["wartetAufDich", "inArbeit", "offen", "erledigtHeute"] as const) {
    const li = document.createElement("li");
    li.className = "zuhause-entwicklung-posten";
    li.setAttribute("data-ticker", k);
    const zahl = document.createElement("span");
    zahl.className = "zuhause-entwicklung-zahl";
    zahl.setAttribute("data-ticker-zahl", "");
    const wort = document.createElement("span");
    wort.className = "zuhause-entwicklung-wort";
    wort.setAttribute("data-ticker-wort", "");
    wort.textContent = WORTE[k];
    li.append(zahl, wort);
    liste.append(li);
  }
  el.append(liste);
  el.hidden = true;
  verwirfWennAlt();
  if (letzte && !verweigert) fuelle(el, letzte);
  return el;
}

/** Die Zahlen in eine Zeile schreiben und sie zeigen. */
function fuelle(el: HTMLElement, z: TickerZahlen): void {
  // Das Wort zuerst: „wartet“ oder „warten“ gehört in die Beschriftung des Feldes.
  const wort = el.querySelector<HTMLElement>('[data-ticker="wartetAufDich"] [data-ticker-wort]');
  if (wort) wort.textContent = wartetWort(z.wartetAufDich);
  for (const k of POSTEN) {
    const posten = el.querySelector<HTMLElement>(`[data-ticker="${k}"]`);
    if (!posten) continue;
    const zahl = posten.querySelector<HTMLElement>("[data-ticker-zahl]");
    if (zahl) zahl.textContent = kurz(z[k]);
    // Null ist leise: das Zeichen tritt zurück, statt zu leuchten.
    posten.toggleAttribute("data-null", z[k] === 0);
    // Jedes Feld sagt sich selbst an („3 warten auf dich“) — es ist kein Knopf und nicht fokussierbar.
    const w = posten.querySelector<HTMLElement>("[data-ticker-wort]")?.textContent ?? "";
    posten.setAttribute("aria-label", `${z[k]} ${w}`);
  }
  const satz = tickerSatz(z);
  el.setAttribute("aria-label", satz);
  el.title = satz;
  el.hidden = false;
}

/** Die Zahlen merken und in jede Zeile im Dokument schreiben (Zuhause baut sie bei Bedarf). */
export function zeichneTicker(z: TickerZahlen): void {
  letzte = z;
  letzteUm = Date.now();
  verweigert = false;
  for (const el of document.querySelectorAll<HTMLElement>(SLOT)) fuelle(el, z);
}

/** Ist die letzte Zählung zu alt, wird sie vergessen und jede Zeile versteckt (Ticket 2848). */
function verwirfWennAlt(): void {
  if (!letzte || istAktuell(letzteUm, Date.now())) return;
  letzte = null;
  for (const el of document.querySelectorAll<HTMLElement>(SLOT)) el.hidden = true;
}

let anhalten: (() => void) | null = null;
/** Wann zuletzt gefragt wurde (ms) — der Abstand gilt für Takt UND Rückkehr. */
let zuletzt = Number.NEGATIVE_INFINITY;

/** Einmal fragen. 403 heisst: nicht für dieses Konto — weg und still. */
async function frage(): Promise<void> {
  try {
    const r = await fetch("/api/admin/ticker", { credentials: "same-origin" });
    if (r.status === 403) {
      verweigert = true;
      letzte = null;
      for (const el of document.querySelectorAll<HTMLElement>(SLOT)) el.hidden = true;
      anhalten?.();
      return;
    }
    const z = r.ok ? zahlenAus(await r.json()) : null;
    if (z) zeichneTicker(z);
    else verwirfWennAlt();
  } catch {
    /* Zuhause darf an dieser Frage nie scheitern — der alte Stand bleibt, solange er aktuell ist. */
    verwirfWennAlt();
  }
}

/**
 * Fragen, wenn jemand hinsieht und die letzte Frage eine Minute her ist.
 *
 * ⚠️ Auch beim Zurückkommen in die App — sonst stünde beim Öffnen bis zu
 *    einer Minute der Stand von vorhin da. Der Abstand gilt trotzdem: wer
 *    die App fünfmal in einer Minute öffnet, löst EINE Frage aus. Zwei
 *    Sekunden Spiel, damit ein Takt, der eine Spur zu früh schlägt, nicht
 *    eine ganze Runde ausfällt.
 */
function vielleichtFragen(): void {
  if (document.visibilityState !== "visible") return;
  // Beim Zurückkehren zuerst: Zahlen von vorhin stehen nicht da, bis die Antwort kommt.
  verwirfWennAlt();
  const jetzt = Date.now();
  if (jetzt - zuletzt < TICKER_TAKT_MS - 2_000) return;
  zuletzt = jetzt;
  void frage();
}

/**
 * Den Ticker starten: gleich fragen (wenn sichtbar), danach im sichtbaren
 * Takt. Ein zweiter Aufruf startet keinen zweiten Takt. Gibt den Abschalter
 * zurück.
 */
export function starteEntwicklungsTicker(): () => void {
  if (anhalten) return anhalten;
  const takt = starteSichtbarenTakt(vielleichtFragen, TICKER_TAKT_MS);
  document.addEventListener("visibilitychange", vielleichtFragen);
  const aus = (): void => {
    takt();
    document.removeEventListener("visibilitychange", vielleichtFragen);
    if (anhalten === aus) anhalten = null;
  };
  anhalten = aus;
  vielleichtFragen();
  return aus;
}
